import React from 'react';
import { Map } from 'immutable';
import ContactField from 'flow/components/contact-card/contact-fields/ContactField';
import ContactNumberField from 'flow/components/contact-card/contact-fields/ContactNumberField';
import ContactAddressField from 'flow/components/contact-card/contact-fields/ContactAddressField';
import useAppContext from 'flow/hooks/useAppContext';
import {
    getFixedNumberFromContact,
    getMobileNumberFromContact,
    getPhoneNumberFromContact,
} from 'flow/utils/extension-utils';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { ExtensionKey } from 'flow/types/EntityKeys';
import ContactEmailField from './contact-fields/ContactEmailField';

interface Props {
    contact: TypedMap<ContactFull>;
    extensionKey?: ExtensionKey;
}

export const ContactInfoFieldsFallback = ({ contact, extensionKey }: Props) => {
    const { i18next } = useAppContext();

    const additionalFieldsGenerator = (fieldEntity: Record<string, string>) => {
        const key = fieldEntity[0];
        const value = fieldEntity[1];
        return (
            <ContactField key={contact.get('key') + '-' + key} label={key}>
                {value}
            </ContactField>
        );
    };

    const mainNumber =
        getFixedNumberFromContact(contact) ||
        getPhoneNumberFromContact(contact);
    const mobileNumber = getMobileNumberFromContact(contact);

    return (
        <main className="flex grow flex-col pb-6">
            <ContactNumberField
                key={contact.get('key') + '-fixed'}
                contact_info_type={i18next.t('ContactCard.Items.FixedPhone')}
                contact_info_value={mainNumber}
                contactToCall={contact}
                extensionKey={extensionKey}
            />
            <ContactNumberField
                key={contact.get('key') + '-mobile'}
                contact_info_type={i18next.t('ContactCard.Items.MobilePhone')}
                contact_info_value={mobileNumber}
                contactToCall={contact}
                extensionKey={extensionKey}
            />
            {contact.get('email') && (
                <ContactEmailField
                    key={contact.get('key') + '-email'}
                    label={i18next.t('ContactCard.Items.Email')}
                >
                    {contact.get('email')}
                </ContactEmailField>
            )}
            {contact.get('companyName') && (
                <ContactField
                    key={contact.get('key') + '-companyName'}
                    label={i18next.t('ContactCard.Items.Company')}
                >
                    {contact.get('companyName')}
                </ContactField>
            )}
            {contact.get('department') && (
                <ContactField
                    key={contact.get('key') + '-department'}
                    label={i18next.t('ContactCard.Items.Department')}
                >
                    {contact.get('department')}
                </ContactField>
            )}
            {contact.get('occupation') && (
                <ContactField
                    key={contact.get('key') + '-occupation'}
                    label={i18next.t('ContactCard.Items.Occupation')}
                >
                    {contact.get('occupation')}
                </ContactField>
            )}
            <ContactAddressField
                key={contact.get('key') + '-address'}
                contact_info_type={i18next.t('ContactCard.Address.Title')}
                contact_info_address_data={contact}
            />
            {contact.get('country') && (
                <ContactField
                    key={contact.get('key') + '-country'}
                    label={i18next.t('ContactCard.Items.Country')}
                >
                    {contact.get('country')}
                </ContactField>
            )}
            {contact.get('description') && (
                <ContactField
                    key={contact.get('key') + '-description'}
                    label={i18next.t('ContactCard.Items.Description')}
                >
                    {contact.get('description')}
                </ContactField>
            )}
            {contact.get('keywords') && (
                <ContactField
                    key={contact.get('key') + '-keywords'}
                    label={i18next.t('ContactCard.Items.Keywords')}
                >
                    {contact.get('keywords')}
                </ContactField>
            )}
            <ContactNumberField
                key={contact.get('key') + '-altTelephone1'}
                contact_info_type={i18next.t('ContactCard.Items.AltPhone1')}
                contact_info_value={contact.getIn(['altTelephone1', 'number'])}
                contactToCall={contact}
                extensionKey={extensionKey}
            />
            <ContactNumberField
                key={contact.get('key') + '-altTelephone2'}
                contact_info_type={i18next.t('ContactCard.Items.AltPhone2')}
                contact_info_value={contact.getIn(['altTelephone2', 'number'])}
                contactToCall={contact}
                extensionKey={extensionKey}
            />
            {contact
                .get('additionalFields', Map())
                .entrySeq()
                .map(additionalFieldsGenerator)}
        </main>
    );
};

export default ContactInfoFieldsFallback;
